'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const faqs = [
  {
    id: 'faq-how-referral',
    question: 'How does the referral work?',
    answer:
      'After joining, you receive a unique referral link (e.g. bidtrekpartner.lc/apply/BT-X7K2M). Share this with truck or pickup owners in Saint Lucia. When they submit their application through your link, they are permanently attributed to your account. Once approved and delivering, every completed delivery earns you a commission.',
  },
  {
    id: 'faq-when-paid',
    question: 'When do I get paid?',
    answer:
      'Payouts are processed once per month. Earnings from deliveries completed during the calendar month are compiled and disbursed within the first week of the following month. Funds are sent directly to the bank account you provide during sign-up. Allow 1–2 business days for your bank to reflect the transfer.',
  },
  {
    id: 'faq-monthly-fee',
    question: 'Is there a monthly fee?',
    answer:
      'No. Your EC$499 membership fee is a one-time, lifetime payment. There are no subscriptions, no renewals, and no hidden charges. You earn for as long as your drivers are active on BidTrek.',
  },
  {
    id: 'faq-driver-rejected',
    question: 'What if my driver is rejected?',
    answer:
      "Your referral code remains fully active. The driver is welcome to reapply once they resolve the issue (e.g. upload a current insurance document). A rejected application has no impact on your membership or any other referrals you've made.",
  },
  {
    id: 'faq-multiple-drivers',
    question: 'Can I refer more than one driver?',
    answer:
      "Yes — you can refer an unlimited number of drivers. All approved drivers contribute to your earnings simultaneously. The more active drivers you refer, the more you earn each payout cycle.",
  },
  {
    id: 'faq-inactive-driver',
    question: 'What happens if a driver goes inactive?',
    answer:
      "If a driver stops delivering, their contribution to your earnings simply stops. Your account and referral code remain active. Your other active drivers continue earning for you. You can also refer new drivers at any time to replace inactive ones.",
  },
  {
    id: 'faq-track-earnings',
    question: 'How do I track my earnings?',
    answer:
      "Your member dashboard shows a real-time breakdown of all accrued earnings, completed payouts, and driver activity. You can see exactly which driver generated each commission and which payout cycle it belongs to.",
  },
  {
    id: 'faq-minimum-payout',
    question: 'Is there a minimum payout?',
    answer:
      'Yes — the minimum payout is EC$20. If your accrued balance is below EC$20 at the end of a cycle, it rolls over automatically to the next cycle. It does not expire or get deducted.',
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>('faq-how-referral');

  return (
    <section id="faq" className="py-24 px-6 bg-[#111111] border-t border-[#2A2A2A]">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#F59E0B] text-sm font-mono font-medium uppercase tracking-widest mb-3">
            FAQ
          </p>
          <h2 className="font-display font-bold text-4xl md:text-5xl text-white">
            Questions & answers
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-2">
          {faqs?.map((faq) => {
            const isOpen = openId === faq?.id;
            return (
              <div
                key={faq?.id}
                className={`border rounded-xl overflow-hidden transition-all duration-200 ${
                  isOpen ? 'border-[#F59E0B]/30 bg-[#1A1A1A]' : 'border-[#2A2A2A] bg-[#1A1A1A]'
                }`}
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                  onClick={() => setOpenId(isOpen ? null : faq?.id)}
                >
                  <span className={`font-semibold text-base transition-colors ${isOpen ? 'text-[#F59E0B]' : 'text-white'}`}>
                    {faq?.question}
                  </span>
                  <Icon
                    name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={18}
                    className={`flex-shrink-0 ml-4 transition-colors ${isOpen ? 'text-[#F59E0B]' : 'text-[#6B7280]'}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 animate-fade-in">
                    <p className="text-[#9CA3AF] text-sm leading-relaxed">{faq?.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}